import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  useMediaQuery,
  CircularProgress,
  Box,
  Tooltip,
  Typography,
  Table,
  TableContainer,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  Paper,
} from "@mui/material";
import { format, addMonths, startOfMonth, isBefore } from "date-fns";
// getJobs
import { getJobs } from "services/api";
import { useTranslation } from "react-i18next";
import { ar, enUS } from "date-fns/locale";

const getDateDiffInDays = (start, end) => Math.ceil((end - start) / (1000 * 60 * 60 * 24));

const MyGantt = ({ initiatives }) => {
  const { i18n } = useTranslation();
  const [groupedTasks, setGroupedTasks] = useState({});
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [months, setMonths] = useState([]);
  const [jobMap, setJobMap] = useState({});
  // const [loading, setLoading] = useState(true);
  const dateFnsLocale = i18n.language === "ar" ? ar : enUS;
  // Calculates the earliest start and latest end date across all initiatives, groups initatives by their type
  // Generates a list of months between minDate and maxDate for the Gantt chart header
  useEffect(() => {
    const fetchInitiatives = async () => {
      if (initiatives?.length === 0) return;
      const jobs = await getJobs();
      const jobMap = Object.fromEntries(jobs?.map((job) => [job.jobId, job.jobName]));

      let min = new Date(initiatives[0].startDate);
      let max = new Date(initiatives[0].endDate);

      //looping over initiatives
      const grouped = initiatives.reduce((acc, item) => {
        const type = item.initiativeType || "Other";
        const start = new Date(item.startDate);
        const end = new Date(item.endDate);
        // const jobName = jobMap[item.jobId] || "Unknown Job";
        //Adds the item to the appropriate group (based on its type)
        if (!acc[type]) acc[type] = [];
        acc[type].push({ ...item, start, end });

        //Updates the overall min and max range based on this item’s dates.
        if (start < min) min = start;
        if (end > max) max = end;

        return acc;
      }, {});

      setGroupedTasks(grouped);
      setMinDate(min);
      setMaxDate(max);

      // Generate month labels from minDate to maxDate,create the month and year headers of the Gantt chart.
      const monthList = [];
      let current = startOfMonth(min);
      while (isBefore(current, max)) {
        monthList.push(current);
        current = addMonths(current, 1);
      }
      setMonths(monthList);
      setJobMap(jobMap);
    };
    //  fetchInitiatives();
    fetchInitiatives().then((e) => {
      // setLoading(false);
    });
  }, [initiatives]);

  const monthsByYear = useMemo(() => {
    return months.reduce((acc, month) => {
      const year = format(month, "yyyy", { locale: dateFnsLocale });
      if (!acc[year]) acc[year] = [];
      acc[year].push(month);
      return acc;
    }, {});
  }, [months, dateFnsLocale]);

  const totalDays = useMemo(() => {
    if (!minDate || !maxDate) return 0;
    return getDateDiffInDays(minDate, maxDate);
  }, [minDate, maxDate]);
  if (!minDate || !maxDate || totalDays === 0) return null;

  //Calculates how far from the start (minDate) this task begins.
  const getLeftPercent = (start) => (getDateDiffInDays(minDate, start) / totalDays) * 100;

  //Calculates how long the initiative lasts.
  const getWidthPercent = (start, end) => (getDateDiffInDays(start, end) / totalDays) * 100;

  const getBarColor = (percent) => {
    if (percent === 100) return "#1B5E20"; // Dark teal green
    if (percent >= 75) return "#388E3C"; // Emerald
    if (percent >= 50) return "#F9A825"; // Mustard yellow
    if (percent >= 20) return "#EF6C00"; // Dark amber
    return "#C2185B"; // Berry red
  };

  return (
    <Box sx={{ overflowX: "auto", px: { xs: 1, sm: 2 }, pt: 1 }}>
      {Object.keys(groupedTasks).map((type) => (
        <Box key={type} sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "primary.main",
              mb: 2,
              borderBottom: "2px solid",
              borderColor: "divider",
              pb: 0.5,
            }}
          >
            {type}
          </Typography>

          <Box sx={{ overflowX: "auto" }}>
            <TableContainer
              component={Paper}
              elevation={2}
              sx={{
                minWidth: 0,
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              }}
            >
              <Table
                stickyHeader
                sx={{
                  tableLayout: "fixed",
                  borderCollapse: "collapse",
                  width: "100%",
                  minWidth: `${(months.length + 1) * 70}px`,
                }}
              >
                <TableBody>
                  {/* Year Header Row */}
                  <TableRow sx={{ "& .MuiTableCell-root": { backgroundColor: "#f9fafb" } }}>
                    <TableCell sx={{ border: "1px solid #e0e0e0" }} />
                    {Object.entries(monthsByYear).map(([year, months]) => (
                      <TableCell
                        key={year}
                        align="center"
                        colSpan={months.length}
                        sx={{
                          fontWeight: 600,
                          border: "1px solid #e0e0e0",
                          fontSize: "0.95rem",
                          color: "text.primary",
                          backgroundColor: "#f0f4f8",
                        }}
                      >
                        {/* {year} */}
                        {i18n.language === "ar"
                          ? new Intl.NumberFormat("ar-EG", { useGrouping: false }).format(year)
                          : year}
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Month Header Row */}
                  <TableRow sx={{ "& .MuiTableCell-root": { backgroundColor: "#fdfdfd" } }}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        border: "1px solid #e0e0e0",
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 100, // 👈 control cutoff here
                      }}
                    >
                      <Tooltip title={i18n.language === "ar" ? "المبادرة" : "Initiative"}>
                        <span style={{ display: "block" }}>
                          {i18n.language === "ar" ? "المبادرة" : "Init.."}
                        </span>
                      </Tooltip>
                    </TableCell>
                    {months.map((month, idx) => (
                      <TableCell
                        key={idx}
                        align="center"
                        sx={{
                          border: "1px solid #e0e0e0",
                          fontSize: "0.875rem",
                          color: "text.secondary",
                        }}
                      >
                        {format(month, "MMM", { locale: dateFnsLocale })}
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Data Rows */}
                  {groupedTasks[type].map((task) => {
                    const left = getLeftPercent(task.start);
                    const width = getWidthPercent(task.start, task.end);

                    return (
                      <TableRow key={task.initiativeId} hover>
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            border: "1px solid #e0e0e0",
                            fontWeight: 500,
                            color: "text.primary",
                            maxWidth: 250, // ensure ellipsis works
                          }}
                        >
                          <Tooltip title={task.initiativeName}>
                            <span>
                              {task.initiativeName.length > 50
                                ? task.initiativeName.slice(0, 50) + "..."
                                : task.initiativeName}
                            </span>
                          </Tooltip>
                        </TableCell>

                        <TableCell
                          colSpan={months.length}
                          sx={{
                            p: 0,
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              height: 20,
                              backgroundColor: "#fff",
                              width: "100%",
                              borderRadius: "8px",
                              overflow: "hidden",
                            }}
                          >
                            <Tooltip
                              title={
                                <>
                                  <div style={{ fontWeight: "bold", marginBottom: 4 }}>
                                    {task.initiativeName}
                                  </div>
                                  <div>
                                    {i18n.language === "ar"
                                      ? `البداية: ${format(task.start, "dd/MM/yyyy", {
                                          locale: dateFnsLocale,
                                        })} • النهاية: ${format(task.end, "dd/MM/yyyy", {
                                          locale: dateFnsLocale,
                                        })}`
                                      : `Start: ${format(task.start, "dd/MM/yyyy", {
                                          locale: dateFnsLocale,
                                        })} • End: ${format(task.end, "dd/MM/yyyy", {
                                          locale: dateFnsLocale,
                                        })}`}
                                  </div>
                                </>
                              }
                              arrow
                            >
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: `${left}%`,
                                  width: `${width}%`,
                                  height: "100%",
                                  backgroundColor: "#f0f0f0",
                                  borderRadius: "8px",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${task.completionPercentage}%`,
                                    height: "100%",
                                    backgroundColor: getBarColor(task.completionPercentage),
                                    color: "#fff",
                                    fontSize: 13,
                                    textAlign: "center",
                                    lineHeight: "28px",
                                    borderRadius: "px",
                                    fontWeight: 600,
                                  }}
                                >
                                  {task.completionPercentage}%
                                </Box>
                              </Box>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

MyGantt.propTypes = {
  initiatives: PropTypes.arrayOf(
    PropTypes.shape({
      initiativeId: PropTypes.number.isRequired,
      initiativeName: PropTypes.string.isRequired,
      initiativeType: PropTypes.string,
      projectManagerName: PropTypes.string,
      startDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      completionPercentage: PropTypes.number,
    })
  ).isRequired,
};

export default MyGantt;
